import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';

import {
  auth,
  db
} from '../utils/firebase';

const EVENTS_COLLECTION = 'hiringEvents';
const REGISTRATIONS_COLLECTION = 'eventRegistrations';

function timestampToMillis(value) {
  if (!value) {
    return 0;
  }

  const date =
    value?.toDate
      ? value.toDate()
      : new Date(value);

  if (isNaN(date.getTime())) {
    return 0;
  }

  return date.getTime();
}

export async function getHiringEvents(
  includeInactive = false
) {
  try {
    const snapshot =
      await getDocs(
        collection(db, EVENTS_COLLECTION)
      );

    return snapshot.docs
      .map(eventDoc => ({
        id: eventDoc.id,
        ...eventDoc.data()
      }))
      .filter(event =>
        includeInactive
          ? true
          : event.active !== false
      )
      .sort(
        (a, b) =>
          timestampToMillis(a.startDateTime) -
          timestampToMillis(b.startDateTime)
      );
  } catch (error) {
    console.error(
      'getHiringEvents error:',
      error
    );

    return [];
  }
}

export async function getHiringEvent(eventId) {
  try {
    const ref =
      doc(db, EVENTS_COLLECTION, eventId);

    const snapshot = await getDoc(ref);

    if (!snapshot.exists()) {
      return null;
    }

    return {
      id: snapshot.id,
      ...snapshot.data()
    };
  } catch (error) {
    console.error(
      'getHiringEvent error:',
      error
    );

    return null;
  }
}

export async function createHiringEvent(eventData) {
  try {
    const organizerId = auth.currentUser?.uid;

    if (!organizerId) {
      throw new Error('User must be logged in.');
    }

    const docRef =
      await addDoc(
        collection(db, EVENTS_COLLECTION),
        {
          ...eventData,
          organizerId,
          active:
            eventData.active !== false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }
      );

    return {
      success: true,
      id: docRef.id
    };
  } catch (error) {
    console.error(
      'createHiringEvent error:',
      error
    );

    return {
      success: false,
      error: error.message
    };
  }
}

export async function updateHiringEvent(
  eventId,
  updates
) {
  try {
    await updateDoc(
      doc(db, EVENTS_COLLECTION, eventId),
      {
        ...updates,
        updatedAt: serverTimestamp()
      }
    );

    return { success: true };
  } catch (error) {
    console.error(
      'updateHiringEvent error:',
      error
    );

    return {
      success: false,
      error: error.message
    };
  }
}

export async function deleteHiringEvent(eventId) {
  try {
    await deleteDoc(
      doc(db, EVENTS_COLLECTION, eventId)
    );

    return { success: true };
  } catch (error) {
    console.error(
      'deleteHiringEvent error:',
      error
    );

    return {
      success: false,
      error: error.message
    };
  }
}

export async function registerForHiringEvent({
  event,
  attendeeType,
  registrationData = {}
}) {
  try {
    const userId = auth.currentUser?.uid;

    if (!userId) {
      throw new Error('User must be logged in.');
    }

    const duplicateQuery = query(
      collection(
        db,
        REGISTRATIONS_COLLECTION
      ),
      where('eventId', '==', event.id),
      where('userId', '==', userId)
    );

    const duplicateSnapshot =
      await getDocs(duplicateQuery);

    if (!duplicateSnapshot.empty) {
      return {
        success: false,
        duplicate: true,
        message:
          'You are already registered for this event.'
      };
    }

    const docRef =
      await addDoc(
        collection(
          db,
          REGISTRATIONS_COLLECTION
        ),
        {
          eventId: event.id,
          eventTitle: event.title || '',
          eventStartDateTime:
            event.startDateTime || '',
          userId,
          attendeeType,
          status: 'Registered',
          checkedIn: false,
          ...registrationData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }
      );

    return {
      success: true,
      id: docRef.id
    };
  } catch (error) {
    console.error(
      'registerForHiringEvent error:',
      error
    );

    return {
      success: false,
      error: error.message
    };
  }
}

export async function getEventRegistrations(eventId) {
  try {
    const q = query(
      collection(
        db,
        REGISTRATIONS_COLLECTION
      ),
      where('eventId', '==', eventId)
    );

    const snapshot = await getDocs(q);

    return snapshot.docs
      .map(registrationDoc => ({
        id: registrationDoc.id,
        ...registrationDoc.data()
      }))
      .sort(
        (a, b) =>
          String(a.organizationName || a.lastName || '')
            .localeCompare(
              String(b.organizationName || b.lastName || '')
            )
      );
  } catch (error) {
    console.error(
      'getEventRegistrations error:',
      error
    );

    return [];
  }
}

export async function getMyEventRegistrations() {
  try {
    const userId = auth.currentUser?.uid;

    if (!userId) {
      return [];
    }

    const q = query(
      collection(
        db,
        REGISTRATIONS_COLLECTION
      ),
      where('userId', '==', userId)
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map(registrationDoc => ({
      id: registrationDoc.id,
      ...registrationDoc.data()
    }));
  } catch (error) {
    console.error(
      'getMyEventRegistrations error:',
      error
    );

    return [];
  }
}

export async function updateEventRegistration(
  registrationId,
  updates
) {
  try {
    await updateDoc(
      doc(
        db,
        REGISTRATIONS_COLLECTION,
        registrationId
      ),
      {
        ...updates,
        updatedAt: serverTimestamp()
      }
    );

    return { success: true };
  } catch (error) {
    console.error(
      'updateEventRegistration error:',
      error
    );

    return {
      success: false,
      error: error.message
    };
  }
}
